import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

// Maps each kPod parameter's raw Firestore key to the label shown to the user.
// Rename the `label` values here to match the terminology used on the other webapp.
// `divide: false` means this field is excluded from the calibration division.
const KPOD_PARAMETER_LABELS = [
  { key: 'startThreshold', label: 'Start Threshold', divide: false },
  { key: 'waterConductivity', label: 'Water Conductivity Threshold', divide: true },
  { key: 'justWaterConductivity', label: 'Just Water Threshold', divide: true },
  { key: 'urineHydratedConductivity', label: 'Hydrated Urine Threshold', divide: true },
  { key: 'urineConductivity', label: 'Urine Conductivity Threshold', divide: true },
];

const DEFAULT_KPOD_VALUES = {
  startThreshold: 200,
  waterConductivity: 180,
  justWaterConductivity: 350,
  urineHydratedConductivity: 400,
  urineConductivity: 350,
};

// Builds the recalculated values (default / scalarFactor, except startThreshold)
// used both as the auto-populated draft and as a guard elsewhere.
const recalculateFromDefaults = (scalarFactor) => {
  const recalculated = {};
  KPOD_PARAMETER_LABELS.forEach(({ key, divide }) => {
    const defaultValue = DEFAULT_KPOD_VALUES[key];
    recalculated[key] = divide ? defaultValue / scalarFactor : defaultValue;
  });
  return recalculated;
};

export const ParametersSection = ({ parameters, calibrationEnabled, calibrationScalarFactor, onSave }) => {
  const currentKPod = parameters?.kPod;
  const [draftKPod, setDraftKPod] = useState(currentKPod || {});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isValidScalarFactor = typeof calibrationScalarFactor === 'number' && calibrationScalarFactor >= 0.01;

  // Whenever the saved values change (unit switch, or after a save), or calibration
  // turns on/off with a valid scalar factor, re-derive the draft: recalculated
  // (default / scalar factor, except Start Threshold) when calibration is active,
  // otherwise just the current values.
  useEffect(() => {
    if (!currentKPod) return;
    if (calibrationEnabled && isValidScalarFactor) {
      setDraftKPod(recalculateFromDefaults(calibrationScalarFactor));
    } else {
      setDraftKPod(currentKPod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKPod, calibrationEnabled, calibrationScalarFactor]);

  if (!currentKPod) return null;

  const loadDefaults = () => {
    setDraftKPod(DEFAULT_KPOD_VALUES);
    setSaveError(null);
  };

  const handleSave = async () => {
    // Parse and validate everything at save time rather than per keystroke, so
    // typing toward e.g. "0.01" isn't blocked at the "0" / "0.0" stage along the way.
    const parsedKPod = {};
    const invalidLabels = [];
    KPOD_PARAMETER_LABELS.forEach(({ key, label }) => {
      const parsed = parseFloat(draftKPod[key]);
      if (isNaN(parsed) || parsed <= 0) {
        invalidLabels.push(label);
      } else {
        parsedKPod[key] = parsed;
      }
    });

    if (invalidLabels.length > 0) {
      setSaveError(`Enter a value greater than 0 for: ${invalidLabels.join(', ')}`);
      return;
    }

    setSaveError(null);
    setIsSaving(true);
    try {
      await onSave(parsedKPod);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form.Group className="text-start col-12" controlId="parameters">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <Form.Label className="mb-0"><strong>PARAMETERS</strong></Form.Label>
        <Button variant="outline-secondary" size="sm" onClick={loadDefaults}>
          Load Default Values
        </Button>
      </div>
      {calibrationEnabled && !isValidScalarFactor && (
        <div className="text-danger small mt-1">
          Calibration is enabled but has no valid Manual Scalar Factor — values below are not recalculated.
        </div>
      )}

      <div className="table-responsive mt-2">
        <table className="table main-background mb-2">
          <thead>
            <tr className="text-start">
              <th className="text-muted">Parameter</th>
              <th className="text-muted">Current</th>
              <th className="text-muted">New value</th>
            </tr>
          </thead>
          <tbody>
            {KPOD_PARAMETER_LABELS.map(({ key, label }) => (
              <tr key={key} className="text-start">
                <td><strong className="text-muted">{label}</strong></td>
                <td className="text-muted">{currentKPod[key] ?? '-'}</td>
                <td style={{ maxWidth: '140px' }}>
                  <Form.Control
                    type="number"
                    size="sm"
                    min="0.01"
                    step="any"
                    value={draftKPod[key] ?? ''}
                    onChange={(e) => {
                      // Always reflect what's typed, including in-progress values
                      // like "0" or "0.0" on the way to "0.01" — validated on Save.
                      const raw = e.target.value;
                      setDraftKPod(prev => ({ ...prev, [key]: raw }));
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {saveError && (
        <div className="text-danger small mb-2">{saveError}</div>
      )}

      <Button variant="success" style={{ color: 'black' }} disabled={isSaving} onClick={handleSave}>
        {isSaving ? 'Saving…' : 'Save Parameters'}
      </Button>
    </Form.Group>
  );
};
